import React, { useEffect, useCallback, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import '../styles/FormModal.css';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onSubmit: () => void;
  submitText?: string;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
}

export const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onSubmit,
  submitText = 'Appliquer',
  isSubmitting = false,
  submitDisabled = false,
}) => {
  // Track if mousedown started on overlay (not inside modal content)
  const mouseDownOnOverlay = useRef(false);
  // Track the element that opened the modal for focus restoration
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Capture trigger element on mount and restore focus on close
  useEffect(() => {
    if (isOpen) {
      // Capture the currently focused element as the trigger
      triggerElementRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // Restore focus to the trigger element when modal closes
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === 'function') {
        triggerElementRef.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  // Only close if both mousedown and click happened on the overlay
  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownOnOverlay.current = e.target === e.currentTarget;
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (mouseDownOnOverlay.current && e.target === e.currentTarget) {
      onClose();
    }
    mouseDownOnOverlay.current = false;
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="form-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <FocusLock returnFocus>
        <div
          className="form-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="form-modal-title"
        >
          <div className="form-modal-header">
            <h2 id="form-modal-title" className="form-modal-title">{title}</h2>
            <button
              type="button"
              className="form-modal-close-btn"
              onClick={onClose}
              aria-label="Fermer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="form-modal-body">
            {children}
          </div>

          <div className="form-modal-footer">
            <button
              type="button"
              className="form-modal-btn form-modal-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="button"
              className="form-modal-btn form-modal-btn-submit"
              onClick={onSubmit}
              disabled={isSubmitting || submitDisabled}
            >
              {isSubmitting ? 'Enregistrement...' : submitText}
            </button>
          </div>
        </div>
      </FocusLock>
    </div>
  );
};
